import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';

import { Divider, tokens, Stack } from '@hotjar/hotsauce';
import { trackClick } from '@hotjar/tracking';
import type { DateRange } from '@hotjar/global-filters';
import { DATE_RANGE_TYPE } from '@hotjar/global-filters';

import { RefreshScreenshotModal } from '../RefreshScreenshotModal/RefreshScreenshotModal';
import { SmartPanelSegment } from './SmartPanelBlocks';
import { SmartPanelSpacedText } from './SmartPanelSpacedText';
import { isSinglePageSearch } from '../../utils/isSinglePageSearch';
import { formattedDate } from '../../utils/formattedDate';
import { EVENT_TRIGGERS } from '../../constants-types/TrackEventTriggers';
import { getTrackGroupDataAndTriggeredOnState } from '../../utils/trackingUtils';
import type { TrackableSite } from '../../constants-types/ExternalDependencyTypes';
import { EVENT_UI_POSITIONS } from '../../constants-types/TrackEventUIPositions';
import type { RetakerMode } from '../../constants-types/ManualRetakerTypes';
import {
  MANUAL_RETAKER_MODE_EVENT_MAP,
  MANUAL_RETAKER_MODES,
} from '../../constants-types/ManualRetakerModes';
import { SmartPanelFullWidthButton } from './SmartPanelFullWidthButton';

interface ScreenshotSegmentProps {
  currentSite?: TrackableSite;
  selectedDateRange: DateRange;
  onRefreshScreenshotClicked: () => void;
  screenshotTimestamp: number | null;
  handleManualRetakerClick: (url: string, mode?: RetakerMode | undefined) => void;
  originUrl: string | null;
  selectedTargetingValue: TargetingValue;
}

const Segment = styled(SmartPanelSegment)`
  flex-direction: column;
`;

const StyledDivider = styled(Divider)`
  margin: ${tokens.spacer.x3} 0 ${tokens.spacer.x2};
`;

export function ScreenshotSegment({
  currentSite,
  selectedDateRange,
  onRefreshScreenshotClicked,
  screenshotTimestamp,
  handleManualRetakerClick,
  originUrl,
  selectedTargetingValue,
}: ScreenshotSegmentProps): JSX.Element | null {
  const { t } = useTranslation('heatmaps');

  const [isRefreshScreenshotModalOpen, setRefreshScreenshotModalOpen] = useState(false);

  const isSinglePage = isSinglePageSearch(selectedTargetingValue);

  const trackRetakeStarted = (mode?: RetakerMode) => {
    if (currentSite !== undefined) {
      trackClick({
        name: mode === undefined ? EVENT_TRIGGERS.REFRESH : MANUAL_RETAKER_MODE_EVENT_MAP[mode],
        uiPosition: EVENT_UI_POSITIONS.SMART_PANEL,
        ...getTrackGroupDataAndTriggeredOnState(currentSite),
      });
    }
  };

  const handleOptionClick = (mode: RetakerMode) => {
    handleManualRetakerClick(
      isSinglePage && originUrl !== null && originUrl.length > 0 ? originUrl : '',
      mode,
    );

    trackRetakeStarted(mode);
  };

  const handleRetakeScreenshotConfirmation = () => {
    onRefreshScreenshotClicked();
    setRefreshScreenshotModalOpen(false);
  };

  const handleRefreshButtonClick = () => {
    setRefreshScreenshotModalOpen(true);
    trackRetakeStarted();
  };

  return (
    <Segment>
      {screenshotTimestamp !== null && (
        <>
          <SmartPanelSpacedText
            leftText={t('smart-panel.screenshot-date')}
            rightText={formattedDate(screenshotTimestamp, 'MMM DD, YYYY')}
          />
          <StyledDivider />
        </>
      )}
      <Stack direction="column" spacing="x2">
        <SmartPanelFullWidthButton icon="replay" kind="ghost" onClick={handleRefreshButtonClick}>
          {t('smart-panel.refresh-screenshot')}
        </SmartPanelFullWidthButton>
        <SmartPanelFullWidthButton
          icon="camera"
          kind="ghost"
          onClick={() => handleOptionClick(MANUAL_RETAKER_MODES.basicScreenshot)}
        >
          {t('smart-panel.retake.take-screenshot')}
        </SmartPanelFullWidthButton>
        <SmartPanelFullWidthButton
          icon="cursor_click"
          kind="ghost"
          onClick={() => handleOptionClick(MANUAL_RETAKER_MODES.dynamicElements)}
        >
          {t('smart-panel.retake.retake-dynamic')}
        </SmartPanelFullWidthButton>
      </Stack>
      <RefreshScreenshotModal
        isSelectedDateRangeAbsolute={selectedDateRange.type === DATE_RANGE_TYPE.ABSOLUTE}
        isOpen={isRefreshScreenshotModalOpen}
        onCancelOrClose={() => setRefreshScreenshotModalOpen(false)}
        onRefreshScreenshot={handleRetakeScreenshotConfirmation}
      />
    </Segment>
  );
}
